import React, { useEffect, useState } from "react";
import { fetchDelegates, manualAddDelegate } from "@/lib/adminApi";
import { PageHeader, StatCard, fmtMoney } from "./_shared";
import LeadTable from "./LeadTable";
import LeadDrawer from "./LeadDrawer";
import { toast } from "sonner";
import { UserPlus, X, Loader2 } from "lucide-react";

const emptyForm = {
  name: "", email: "", phone: "", organisation: "", role: "",
  country: "India", interest: "",
  pass_type: "conference", delegate_type: "indian",
  total_amount: 0, currency: "INR", note: "",
};

function AddDelegateForm({ onClose, onAdded }) {
  const [f, setF] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) { toast.error("Name & email required"); return; }
    setSaving(true);
    try {
      const created = await manualAddDelegate({ ...f, total_amount: Number(f.total_amount) || 0 });
      toast.success("Delegate added");
      onAdded && onAdded(created);
      setF(emptyForm);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-lime-500";
  const lbl = "block text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-1";

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-lg bg-white shadow-2xl border-l border-slate-200 flex flex-col">
        <div className="border-b border-slate-100 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-lime-600">Manual</div>
            <div className="font-display text-lg font-bold text-slate-900">Add delegate</div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Full name *</label><input required data-testid="add-name" className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><label className={lbl}>Email *</label><input required type="email" data-testid="add-email" className={inp} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><label className={lbl}>Phone</label><input data-testid="add-phone" className={inp} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><label className={lbl}>Organisation</label><input data-testid="add-org" className={inp} value={f.organisation} onChange={(e) => set("organisation", e.target.value)} /></div>
            <div><label className={lbl}>Role</label><input className={inp} value={f.role} onChange={(e) => set("role", e.target.value)} /></div>
            <div><label className={lbl}>Country</label><input className={inp} value={f.country} onChange={(e) => set("country", e.target.value)} /></div>
            <div><label className={lbl}>Interest / Segment</label><input className={inp} value={f.interest} onChange={(e) => set("interest", e.target.value)} /></div>
            <div><label className={lbl}>Delegate type</label>
              <select className={inp} value={f.delegate_type} onChange={(e) => { set("delegate_type", e.target.value); set("currency", e.target.value === "foreign" ? "USD" : "INR"); }}>
                <option value="indian">Indian</option>
                <option value="foreign">International</option>
              </select>
            </div>
            <div><label className={lbl}>Pass type</label>
              <select className={inp} value={f.pass_type} onChange={(e) => set("pass_type", e.target.value)}>
                <option value="conference">Conference</option>
                <option value="expo">Expo</option>
                <option value="full">Full Access</option>
                <option value="vip">VIP</option>
                <option value="complimentary">Complimentary</option>
              </select>
            </div>
            <div><label className={lbl}>Amount ({f.currency})</label><input type="number" data-testid="add-amount" className={inp} value={f.total_amount} onChange={(e) => set("total_amount", e.target.value)} /></div>
          </div>
          <div><label className={lbl}>Internal note</label><textarea rows={2} className={inp} placeholder="Comp / speaker / media pass reason…" value={f.note} onChange={(e) => set("note", e.target.value)} /></div>
        </div>
        <div className="border-t border-slate-100 p-3 flex gap-2">
          <button data-testid="add-submit" disabled={saving} className="flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add delegate
          </button>
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default function DelegatesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    fetchDelegates("paid").then((d) => { setRows(d); setLoading(false); });
  }, []);

  const totalInr = rows.filter(r => r.extra?.currency === "INR").reduce((s, r) => s + (r.extra?.total_amount || 0), 0);
  const totalUsd = rows.filter(r => r.extra?.currency === "USD").reduce((s, r) => s + (r.extra?.total_amount || 0), 0);
  const uncontacted = rows.filter(r => !r.extra?.contacted).length;

  return (
    <div data-testid="page-delegates">
      <PageHeader
        title="Confirmed Delegates"
        subtitle="Paid & verified attendees — assign to team, send welcome kits, resend confirmations."
        actions={
          <button data-testid="open-add-delegate" onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
            <UserPlus className="h-4 w-4" /> Add delegate
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard tone="lime" label="Total confirmed" value={rows.length} />
        <StatCard tone="navy" label="Revenue INR" value={fmtMoney(totalInr, "INR")} />
        <StatCard label="Revenue USD" value={fmtMoney(totalUsd, "USD")} />
        <StatCard tone="amber" label="Awaiting outreach" value={uncontacted} sub="Not yet contacted" />
      </div>

      {loading ? <div className="text-sm text-slate-500">Loading…</div> : (
        <LeadTable
          rows={rows}
          setRows={setRows}
          onRowClick={setActive}
          showFormType={false}
          showPayment
          filename="bef2026-delegates"
          emptyText="No confirmed delegates yet. Click 'Add delegate' to create one manually."
        />
      )}

      <LeadDrawer
        lead={active}
        onClose={() => setActive(null)}
        onChange={(u) => { setRows((prev) => prev.map((x) => x.id === u.id ? u : x)); setActive(u); }}
        onDelete={(id) => setRows((prev) => prev.filter((x) => x.id !== id))}
      />

      {showAdd && (
        <AddDelegateForm
          onClose={() => setShowAdd(false)}
          onAdded={(created) => setRows((prev) => [created, ...prev])}
        />
      )}
    </div>
  );
}
